import {
  CloudOff,
  Compass,
  FileCheck2,
  Factory,
  HardHat,
  Radio,
  Ruler,
  ShieldCheck,
  Wrench,
  type LucideIcon,
} from "lucide-react";

/**
 * The marketing site's information architecture, in one place.
 *
 * The header and the footer render the same tree from here rather than keeping
 * two hand-maintained lists. A page added to one and forgotten in the other is
 * the single most common way a marketing site ends up with an unreachable page,
 * and it is invisible in review because both files look complete on their own.
 *
 * Every `to` is a real route or an anchor inside one. Nothing here links to a
 * page that does not exist - a dead nav item costs more trust than a missing
 * one, because the visitor blames the product rather than the roadmap.
 */

export interface NavLeaf {
  to: string;
  label: string;
  blurb: string;
  icon: LucideIcon;
}

export interface NavGroup {
  label: string;
  /** Where the group's own name goes when it is clicked rather than hovered. */
  to: string;
  items: NavLeaf[];
}

export const PRODUCT_NAV: NavGroup = {
  label: "Product",
  to: "/product",
  items: [
    {
      to: "/product#area-scan",
      label: "Area scan",
      blurb: "A 20–30 second sweep returns a plain-language hazard list",
      icon: Compass,
    },
    {
      // The route id stays `ladder-angle` - it is a real anchor and possibly
      // bookmarked - even though the label no longer matches it. The label is
      // the module's own name now, because the section covers the whole of it:
      // the inclinometer reading *and* the set-up checks a model reads off the
      // photographs. "Angle check" named the half a sensor answers.
      to: "/product#ladder-angle",
      label: "Working at heights",
      blurb: "The setup angle from the inclinometer, the rest from the photographs",
      icon: Ruler,
    },
    {
      to: "/product#ppe",
      label: "PPE check",
      blurb: "Seven items read off one photograph - found, not there, or not in shot",
      icon: ShieldCheck,
    },
    {
      to: "/product#offline",
      label: "Works with no signal",
      blurb: "Capture and checks run on the device, sync when coverage returns",
      icon: CloudOff,
    },
    {
      to: "/product#record",
      label: "The record",
      blurb: "Timestamped, geotagged, exportable - the reason the office cares",
      icon: FileCheck2,
    },
  ],
};

export const SOLUTIONS_NAV: NavGroup = {
  label: "Solutions",
  to: "/solutions",
  items: [
    {
      to: "/solutions#utilities",
      label: "Utilities",
      blurb: "Overhead lines, poles and service drops",
      icon: Factory,
    },
    {
      to: "/solutions#telecom",
      label: "Telecom & fibre",
      blurb: "Aerial builds, splice work and rooftop nodes",
      icon: Radio,
    },
    {
      to: "/solutions#facilities",
      label: "Facilities & HVAC",
      blurb: "Rooftop units, plant rooms and confined spaces",
      icon: Wrench,
    },
    {
      to: "/solutions#contractors",
      label: "Contractors",
      blurb: "Crews across many client sites, one record each",
      icon: HardHat,
    },
  ],
};

/** Flat entries, rendered as plain links beside the two menus. */
export const FLAT_NAV = [
  { to: "/pricing", label: "Pricing" },
  { to: "/company", label: "Company" },
] as const;

export const FOOTER_NAV: { heading: string; items: { to: string; label: string }[] }[] = [
  {
    heading: "Product",
    items: [
      { to: "/product", label: "How it works" },
      { to: "/product#record", label: "The record" },
      { to: "/pricing", label: "Pricing" },
      { to: "/product#limits", label: "What we don't claim" },
    ],
  },
  {
    heading: "Solutions",
    items: SOLUTIONS_NAV.items.map(({ to, label }) => ({ to, label })),
  },
  {
    heading: "Company",
    items: [
      { to: "/company", label: "About Visionify" },
      { to: "/company#contact", label: "Contact" },
    ],
  },
  {
    heading: "Get started",
    items: [
      { to: "/signup", label: "Create an account" },
      { to: "/login", label: "Sign in" },
      { to: "https://visionify.ai/privacy", label: "Privacy" },
      { to: "https://visionify.ai/terms", label: "Terms" },
      { to: "/delete-account", label: "Delete account" },
    ],
  },
];
